import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import AlertDialog from 'components/UI/AlertDialog';
import { PrimaryBtn } from 'components/UI/Buttons';
import { type FC, useState } from 'react';
import { useCreateFollowUpMutation } from 'services/api/followUpConfig.api';
import type { FollowUpConfig, FollowUpConfigDraft } from 'services/types/followUpConfig';

import FollowUpConfigFormAttributes from './FollowUpConfigFormAttributes';

interface CreateFollowUpConfigProps {
  businessUnitId: number;
  onSuccess?: (followUpConfig: FollowUpConfig) => void;
  onCancel?: () => void;
  mode?: 'general' | 'campaign';
  initialData?: Partial<FollowUpConfigDraft>;
  showCancelButton?: boolean;
  title?: string;
  submitButtonText?: string;
}

const CreateFollowUpConfig: FC<CreateFollowUpConfigProps> = ({
  businessUnitId,
  onSuccess,
  onCancel,
  mode = 'general',
  initialData = {},
  showCancelButton = false,
  title = 'Crear Nuevo Follow Up',
  submitButtonText = 'Crear Follow Up',
}) => {
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [createFollowUpConfig, { isLoading }] = useCreateFollowUpMutation();

  const [followUpConfigDraft, setFollowUpConfigDraft] = useState<FollowUpConfigDraft>({
    prompt: null,
    specificTexts: null,
    templateName: null,
    businessUnitId,
    minMinutesWaitTime: mode === 'campaign' ? 10 : 60,
    maxMinutesWaitTime: mode === 'campaign' ? 129_600 : 120, // 3 months for campaign, 2 hours for general
    minWorkingHour: 9,
    maxWorkingHour: 18,
    sendOnWeekend: false,
    identifier: '',
    maxConsecutive: 1,
    maxAmountInConversation: 1,
    previousMessageKeepAliveIdentifier: null,
    requiredPreviousFollowUpsIds: null,
    conditionsToSend: JSON.stringify(mode === 'campaign' ? { lead: { outboundCampaignId: '' } } : {}, null, 2),
    templateComponents: null,
    isActive: true,
    clientsBatchSize: mode === 'campaign' ? 50 : 10,
    functionName: null,
    imageUrl: null,
    templatesToChoose: null,
    surveyConfig: null,
    ...initialData,
  });

  // Helper function to get message type
  const getMessageType = () => {
    if (followUpConfigDraft.prompt !== null) return 'prompt';
    if (followUpConfigDraft.specificTexts !== null) return 'specificTexts';
    if (followUpConfigDraft.templateName !== null) return 'templateName';
    return null;
  };

  // Validation function
  const validateForm = () => {
    const messageType = getMessageType();
    if (!messageType) {
      setErrorMessage('Debes seleccionar un tipo de mensaje (Prompt, Textos específicos, o Plantilla)');
      setIsError(true);
      return false;
    }

    if (!followUpConfigDraft.identifier.trim()) {
      setErrorMessage('El identificador es requerido');
      setIsError(true);
      return false;
    }

    if (followUpConfigDraft.maxMinutesWaitTime - followUpConfigDraft.minMinutesWaitTime <= 10) {
      setErrorMessage('El tiempo máximo debe ser al menos 10 minutos mayor que el tiempo mínimo');
      setIsError(true);
      return false;
    }

    if (followUpConfigDraft.minWorkingHour >= followUpConfigDraft.maxWorkingHour) {
      setErrorMessage('La hora mínima debe ser menor que la hora máxima');
      setIsError(true);
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const submitData = {
        ...followUpConfigDraft,
        conditionsToSend: JSON.parse(followUpConfigDraft.conditionsToSend) as Record<string, unknown>,
        templateComponents: followUpConfigDraft.templateComponents
          ? (JSON.parse(followUpConfigDraft.templateComponents) as Record<string, unknown>)
          : null,
      } as unknown as FollowUpConfigDraft;
      const result = await createFollowUpConfig(submitData).unwrap();
      setTimeout(() => {
        if (onSuccess) {
          onSuccess(result);
        }
      }, 2000);
    } catch (error: unknown) {
      // Extract user-friendly error message from RTK Query error
      const extractErrorMessage = (err: unknown): string => {
        if (err && typeof err === 'object') {
          // RTK Query error structure: error.data contains the message string
          if ('data' in err && typeof err.data === 'string') {
            return err.data;
          }
          // Fallback: check for message in nested structure
          if ('data' in err && err.data && typeof err.data === 'object' && 'message' in err.data) {
            return String(err.data.message);
          }
        }
        return 'Error al crear el follow up';
      };
      setErrorMessage(extractErrorMessage(error));
      setIsError(true);
    }
  };

  const handleMessageTypeChange = (type: 'prompt' | 'specificTexts' | 'templateName') => {
    setFollowUpConfigDraft({
      ...followUpConfigDraft,
      prompt: type === 'prompt' ? '' : null,
      specificTexts: type === 'specificTexts' ? [] : null,
      templateName: type === 'templateName' ? '' : null,
    });
  };

  return (
    <Box sx={{ mb: 2 }}>
      <AlertDialog
        openDialog={isError}
        setOpenDialog={setIsError}
        header="Error al crear follow up"
        msg={errorMessage}
        cancelMsg="Cerrar"
      />
      <Card sx={{ mb: 1.5, boxShadow: 0, borderRadius: 0 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 1.5, fontWeight: 600 }}>
            {title}
          </Typography>

          <Grid container spacing={2}>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>Tipo de mensaje</InputLabel>
                <Select
                  value={getMessageType() || ''}
                  onChange={(e) =>
                    handleMessageTypeChange(e.target.value as 'prompt' | 'specificTexts' | 'templateName')
                  }
                  label="Tipo de mensaje"
                >
                  <MenuItem value="prompt">Instrucciones (Prompt)</MenuItem>
                  <MenuItem value="specificTexts">Textos específicos</MenuItem>
                  <MenuItem value="templateName">Nombre de la plantilla</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <FollowUpConfigFormAttributes
        followUpConfigDraft={followUpConfigDraft}
        setFollowUpConfigDraft={setFollowUpConfigDraft}
        isEditing
        isCampaign={mode === 'campaign'}
        businessUnitId={businessUnitId}
      />
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mt: 2,
        }}
      >
        <PrimaryBtn
          onClick={() => {
            handleSubmit().catch((error) => {
              console.error('Error submitting form:', error);
            });
          }}
          startIcon={<AddIcon />}
          variant="contained"
          disabled={isLoading || !followUpConfigDraft.identifier}
        >
          {isLoading ? 'Creando...' : submitButtonText}
        </PrimaryBtn>
        {showCancelButton && onCancel && (
          <PrimaryBtn onClick={onCancel} variant="outlined">
            Cancelar
          </PrimaryBtn>
        )}
      </Box>
    </Box>
  );
};

export default CreateFollowUpConfig;
